import {ReloadOutlined, SearchOutlined} from '@ant-design/icons/lib';
import {Button} from 'antd';
import {BillingPlans, RiskProfile, SalesExecutive, useAccountTagsQuery} from 'api/generated';
import {SearchBar} from 'components';
import {Formik, FormikHelpers} from 'formik';
import {Form, Input, Select} from 'formik-antd';
import {useAccessLevel} from 'hooks/useAccessLevel';
import {useCountries} from 'hooks/useCountries';
import humanize from 'humanize-string';
import React from 'react';
import SelectMasterAccounts from '../../components/SelectMasterAccounts';
import {formatHumanName} from '../../lib/formaters';
import {isEmpty} from '../../lib/omitEmpty';

type FilterValues = {
  filterName: string;
  filterValue: string;
};

export type FilterQueryParams = {
  [key: string]: any;
};

type Props = {
  onChange: (values: FilterQueryParams) => void;
  queryFilters: FilterQueryParams;
  availableFilters?: Record<string, string>;
};

const adminOnlyFilters = [
  'secret',
  'apiKey',
  'dynamicBilling',
  'payoutTermsSuspended',
  'billingPlan',
  'referralCode',
  'promoCode',
  'masterAccountId',
  'churned',
  'blocked'
];

export const AccountFilters: React.FC<Props> = ({onChange, queryFilters, availableFilters}) => {
  const {supportedCountries} = useCountries();
  const {isAdmin} = useAccessLevel();

  const {data: accountTagsData} = useAccountTagsQuery({skip: !isAdmin});

  const {...filters} = queryFilters;

  const initialValues: FilterValues = {
    filterName: 'name',
    filterValue: ''
  };

  const getInput = (filterName: string) => {
    const name = 'filterValue';
    switch (filterName) {
      case 'businessCountry':
        return (
          <Select name={name} style={{width: '100%'}} placeholder="Select country" allowClear>
            {supportedCountries.map((country) => (
              <Select.Option value={country.code}>{country.name}</Select.Option>
            ))}
          </Select>
        );
      case 'billingPlan':
        return (
          <Select name={name} style={{width: '100%'}} placeholder="Select billing plan" allowClear>
            {Object.values(BillingPlans).map((plan) => (
              <Select.Option value={plan}>{plan.replace('_', ' ')}</Select.Option>
            ))}
          </Select>
        );
      case 'dynamicBilling':
      case 'payoutTermsSuspended':
      case 'churned':
      case 'blocked':
        return (
          <Select name={name} style={{width: '100%'}} placeholder="Select option" allowClear>
            <Select.Option value={true}>True</Select.Option>
            <Select.Option value={false}>False</Select.Option>
          </Select>
        );
      case 'masterAccountId':
        return <SelectMasterAccounts name={name} />;
      case 'tags':
        return (
          <Select
            style={{width: '100%'}}
            name={name}
            allowClear
            showSearch
            placeholder="Select tag">
            {accountTagsData?.accountTags.map((tag) => (
              <Select.Option value={tag}>{tag}</Select.Option>
            ))}
          </Select>
        );
      case 'riskProfile':
        return (
          <Select name={name} style={{width: '100%'}} placeholder="Select option" allowClear>
            {Object.values(RiskProfile).map((riskProfile) => (
              <Select.Option value={riskProfile}>{humanize(riskProfile)}</Select.Option>
            ))}
          </Select>
        );
      case 'accountExecutive':
        return (
          <Select
            name={name}
            style={{width: '100%'}}
            placeholder="Select account executive"
            allowClear>
            {Object.values(SalesExecutive).map((accountExecutive) => (
              <Select.Option value={accountExecutive}>
                {formatHumanName(accountExecutive)}
              </Select.Option>
            ))}
          </Select>
        );
    }
    return <Input name={name} allowClear />;
  };

  const handleSubmit = (
    {filterName, filterValue}: FilterValues,
    {resetForm}: FormikHelpers<typeof initialValues>
  ) => {
    const filter: FilterQueryParams = {...filters};
    if (filterValue !== '') filter[filterName] = filterValue;
    if (!isEmpty(filter)) {
      filter['status'] = 'ANY';
    }
    setTimeout(resetForm, 100);
    return onChange(filter);
  };

  const isSelected = (value: string) =>
    queryFilters.hasOwnProperty(value) &&
    queryFilters[value] !== undefined &&
    queryFilters[value] !== null;

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
      {({dirty, values}) => (
        <Form>
          <SearchBar compact>
            <Select
              name="filterName"
              style={{minWidth: 250}}
              showSearch
              optionFilterProp="children"
              listHeight={500}>
              {Object.entries(availableFilters).map(([value, label]) => {
                if ((adminOnlyFilters.includes(value) && !isAdmin) || isSelected(value))
                  return null;
                return (
                  <Select.Option key={value} value={value}>
                    {label}
                  </Select.Option>
                );
              })}
            </Select>
            {getInput(values.filterName)}
            <Button htmlType="submit" style={{minWidth: 130}} type={dirty ? 'primary' : undefined}>
              {dirty ? (
                <>
                  <SearchOutlined /> Search
                </>
              ) : (
                <>
                  <ReloadOutlined /> Refresh
                </>
              )}
            </Button>
          </SearchBar>
        </Form>
      )}
    </Formik>
  );
};
